import StyledButton from "ae-base/Base/StyledButton";
import StyledText from "ae-base/Base/StyledText";
import React, { PureComponent } from "react";
import { View } from "react-native";
import ResponsiveStylesheet from "react-native-responsive-stylesheet";
import ErrorMessage from "./Layout/ErrorMessage";
import InputContainer from "./Layout/InputContainer";
import InputLabel from "./Layout/InputLabel";
import Style from "ae-style";
import { useMeasureUnits, useResources, useIdPrefix } from "@aetonix/hooks";

const VALID_INDICATORS = ["activity", "bodytemperature", "bloodpressure", "bloodsugar", "bloodoxygen", "weight"];

const styles = ResponsiveStylesheet.createSized("min-direction", {
	0: {
		reDoLink: {
			textDecorationLine: "underline"
		},
		textStyle: {
			padding: Style.layout.paddingList,
			textAlign: "center",
			fontSize: Style.text.emphasized,
			width: "100%",
			paddingBottom: 16
		},
		container: {
			flexDirection: "column",
			justifyContent: "center",
			flex: 1,
			flexWrap: "wrap",
			marginTop: 0
		},
		buttonContainer: {
			flexDirection: "row",
			justifyContent: "flex-end",
			flex: 1,
			flexWrap: "wrap",
			marginTop: Style.layout.marginContentVertical
		},
		buttonStyle: {
			justifyContent: "flex-end"
		},
		reDoTextStyle: {
			marginTop: 0,
			marginBottom: 0,
			width: "100%",
			color: "#515151"
		}
	},
	512: {
		reDoTextStyle: {
			marginTop: 20,
			marginBottom: -20
		},
		container: {
			marginTop: 24
		},
	}
});


export default function Reading(props){
	const measureUnits = useMeasureUnits();
	const { localize, dispatch, listen, unlisten } = useResources();
	const idPrefix = useIdPrefix();
	return (
		<ReadingComp
			{...props}
			measureUnits={measureUnits}
			localize={localize}
			dispatch={dispatch}
			listen={listen}
			unlisten={unlisten}
			id_prefix={idPrefix}
		/>
	);
}
class ReadingComp extends PureComponent {
	constructor(props) {
		super(props);
		this._updateReading = this.updateReading.bind(this);
		this.state = {
			hasChanged: false,
			value: null
		};

		this.makeLegible = makeLegible.bind(this);
	}
	componentDidMount() {
		const props = this.props;
		const listen = props.listen;
		const indicator = (props.schema || {}).indicator;
		listen("myhealthdevice:" + indicator + ":reading", this._updateReading);
	}
	componentWillUnmount() {
		const props = this.props;
		const unlisten = props.unlisten;
		const indicator = (props.schema || {}).indicator;
		unlisten("myhealthdevice:" + indicator + ":reading", this._updateReading);
	}

	updateReading(reading) {
		const value = convertValues(reading.value, (this.props.schema || {}).indicator);
		this.setState({
			hasChanged: true,
			value: value
		});
		this.props.onChange(value);
	}

	render() {
		const props = this.props;
		const localize = props.localize;
		const dispatch = props.dispatch;
		const schema = props.schema;
		const indicator = schema.indicator;
		if (!indicator) return null;
		if (VALID_INDICATORS.indexOf(indicator) === -1) return null;

		const value = this.state.value;

		const reading = localize("myhealthdevice.readinglabel") + this.makeLegible(value, indicator);
		const doReading = localize("myhealthdevice.read");
		const errors = props.errors;
		const triggerNewReading = dispatch("myhealthdevice:trigger:workflow", {
			deviceType: indicator
		});

		return (
			<InputContainer errors={errors}>
				<InputLabel schema={schema} />
				<ErrorMessage id={props.id_prefix + props.id + "_error"} errors={errors} schema={schema} />
				{value ? (
					<View>
						<View style={styles.container}>
							<StyledText id={props.id + "_value"} black style={styles.textStyle}>
								{reading}
							</StyledText>
						</View>
						<View>
							<StyledText style={styles.reDoTextStyle}>
								{localize("myhealthdevice.redoreadingtext")}
								<StyledText style={styles.reDoLink} onPress={triggerNewReading}>
									{localize("myhealthdevice.redoreadingclick")}
								</StyledText>
							</StyledText>
						</View>
					</View>
				) : (
					<View style={styles.buttonContainer}>
						<StyledButton
							id={props.id + "_button"}
							title={doReading}
							style={styles.buttonStyle}
							contrast
							onPress={triggerNewReading}
						/>
					</View>
				)}
			</InputContainer>
		);
	}
}

function convertValues(value, type) {
	if (!(value || value === 0) || !type) return {};
	if (type === "bodytemperature") return convertBodyTemperature(value.temperature, value.units);
	else if (type === "bloodsugar") return convertBloodSugar(value.glucose, value.units);
	else if (type === "weight") return convertWeight(value.weight, value.units);
	else return value;
}

function convertBodyTemperature(value, units) {
	const returnValue = {
		units: units
	};
	if (units === "celsius") {
		returnValue.celsius = parseValue(value, 1);
		returnValue.fahrenheit = cToF(value);
	} else if (units === "fahrenheit") {
		returnValue.celsius = fToC(value);
		returnValue.fahrenheit = parseValue(value, 1);
	}
	return returnValue;
}

function convertBloodSugar(value, units) {
	const returnValue = {
		units: units
	};
	if (units === "mg") {
		returnValue.mg = parseValue(value, 2);
		returnValue.mmol = mgToMmol(value);
	} else if (units === "mmol") {
		returnValue.mg = mmolToMg(value);
		returnValue.mmol = parseValue(value, 2);
	}
	return returnValue;
}

function convertWeight(value, units) {
	const returnValue = {
		units: units
	};
	if (units === "kg") {
		returnValue.kg = parseValue(value, 2);
		returnValue.lbs = kgToLbs(value);
	} else if (units === "lbs") {
		returnValue.kg = lbsToKg(value);
		returnValue.lbs = parseValue(value, 2);
	}
	return returnValue;
}

function makeLegible(value, type) {
	const { weightUnits, glucometerUnits, temperatureUnits } = this.props.measureUnits;

	if (!value || !type) return "";
	if (type === "bodytemperature") {
		return value[temperatureUnits] + "\u00B0" + temperatureUnits[0].toUpperCase();
	} else if (type === "activity") {
		return value.value + "";
	} else if (type === "bloodpressure") {
		return value.systolic + "/" + value.diastolic + "mmHg" + " - " + value.bpPulserate + "bpm";
	} else if (type === "bloodsugar") {
		const displayUnits = glucometerUnits === "mg" ? "mg/dL" : "mmol/L";
		return value[glucometerUnits] + " " + displayUnits;
	} else if (type === "bloodoxygen") {
		return value.pulserate + " bpm, " + value.oxygensaturation + "%";
	} else if (type === "weight") {
		return value[weightUnits] + " " + weightUnits;
	} else {
		return "";
	}
}

function fToC(f) {
	const c = parseFloat((f - 32) * 0.556).toFixed(1);
	return c;
}

function cToF(c) {
	const f = parseFloat(c * 1.8 + 32).toFixed(1);
	return f;
}

function mmolToMg(mmol) {
	const mg = (mmol * 18).toFixed(2);
	return mg;
}

function mgToMmol(mg) {
	const mmol = (mg / 18).toFixed(2);
	return mmol;
}

function lbsToKg(lbs) {
	const kg = parseFloat(lbs * 0.454).toFixed(2);
	return kg;
}

function kgToLbs(kg) {
	const lbs = parseFloat(kg / 0.454).toFixed(2);
	return lbs;
}

function parseValue(value, toFixed) {
	return parseFloat(value).toFixed(toFixed);
}